import { useEffect, useMemo, useRef } from 'react';
import { NetworkStatus, useQuery, useSubscription } from '@apollo/client';
import { useIsFocused } from '@react-navigation/native';

import { CLOCK_EVENT_ACTION } from '../../../common/constants';
import {
	GET_MY_CLOCK_STATUS,
	SUBSCRIBE_TO_CLOCK_EVENT_SCAN,
} from '../../../common/graphql/clockEvent';
import { useUserContext } from '../../../common/utils/context';
import { useOfflineQueue } from '../../offline';

/**
 * Live clock status for the current user, normalized to the shape consumed by
 * `src/common/utils/myDay/driverDaySteps.js` ({ isClockedIn, hasClockedOutToday }).
 * Refetches on screen focus and on kiosk scan events (subscription skipped in
 * performance mode, mirroring ClockPassView).
 * `isFocused` overrides the screen focus for callers mounted outside a single
 * screen (the driver day provider passes "a driver screen is focused").
 */
const useMyClockStatus = ({ skip = false, isFocused: isFocusedOverride } = {}) => {
	const isScreenFocused = useIsFocused();
	const isFocused = isFocusedOverride ?? isScreenFocused;
	const { user, isAVC } = useUserContext();
	const { isOnline, isReady } = useOfflineQueue();
	const hasFocusedOnceRef = useRef(false);

	const canUseClockStatus = Boolean(isAVC && !skip);

	const { data, error, loading, networkStatus, refetch } = useQuery(GET_MY_CLOCK_STATUS, {
		// Offline remounts must reuse the last known clock status instead of
		// blanking it; focus/scan refetches keep it fresh online.
		fetchPolicy: 'cache-and-network',
		errorPolicy: 'all',
		notifyOnNetworkStatusChange: true,
		skip: !canUseClockStatus,
	});

	const { data: scanSubscriptionData } = useSubscription(SUBSCRIBE_TO_CLOCK_EVENT_SCAN, {
		variables: {
			input: {},
		},
		skip: !canUseClockStatus || Boolean(user?.settings?.performanceMode),
	});

	useEffect(() => {
		if (!isFocused || !canUseClockStatus) {
			return;
		}

		if (!hasFocusedOnceRef.current) {
			hasFocusedOnceRef.current = true;
			return;
		}

		refetch();
	}, [canUseClockStatus, isFocused, refetch]);

	const latestScannedClockEventId = scanSubscriptionData?.ClockEventScanSubscription?.clockEvent?.id;
	useEffect(() => {
		if (!latestScannedClockEventId || !canUseClockStatus) {
			return;
		}

		refetch();
	}, [canUseClockStatus, latestScannedClockEventId, refetch]);

	const clockStatus = useMemo(() => {
		const status = data?.getMyClockStatus;
		return {
			isClockedIn: Boolean(status?.isClockedIn),
			hasClockedOutToday: Number(status?.lastEventAction) === CLOCK_EVENT_ACTION.CLOCK_OUT,
			lastEventAction: status?.lastEventAction ?? null,
			lastEventCapturedAt: status?.lastEventCapturedAt ?? null,
		};
	}, [data?.getMyClockStatus]);
	// A cached or failed result is useful for the status chip, but must not hide
	// cached My Day work. Only a completed successful response can gate the board.
	const isClockStatusReliable = Boolean(canUseClockStatus &&
		isReady &&
		isOnline &&
		data?.getMyClockStatus &&
		networkStatus === NetworkStatus.ready &&
		!error);

	return {
		clockStatus,
		loading,
		refetch,
		canUseClockStatus,
		isClockStatusReliable,
	};
};

export default useMyClockStatus;
